import logo from '@assets/appImages/favicon.png';
import { useActiveFeatureFlags } from '@infrastructure/featureFlags';
import { NAVIGATION_CONFIG, sectionGateCapabilities } from '@config/navigation.config';
import { useAIAssistantSelector, useAIAssistantActions } from '@domains/aiAssistant';
import useAuth, { AuthContextType } from '@contexts/useAuth/useAuth';
import useUserPreferences, { UserPreferencesContextType } from '@contexts/useUserPreferences/useUserPreferences';
import { useNavbarOverflow } from '@hooks/useNavbarOverflow';
import { Accordion, Badge, Burger, Divider, Drawer, Group, Image, Menu, rem, Text, UnstyledButton, useMantineColorScheme, useComputedColorScheme } from '@mantine/core';
import { IconChevronRight, IconDots, IconLanguage, IconLogout, IconMinus, IconMoon, IconProgressHelp, IconSun } from '@tabler/icons-react';
import { useBooleanFlagValue } from '@openfeature/react-sdk';
import { stringToLowerCaseDashed } from '@utils/strings';
import { Fragment, useMemo, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { useLocation } from 'react-router';
import { useNavigationDispatch } from '@domains/navigation';
import NavbarLink from '@components/common/NavbarLink';
import SparkleIcon from '@components/common/SparkleIcon';
import { AccountMenu } from './AccountMenu';
import { ObservationModePill } from './ObservationModePill/ObservationModePill';
import { useAccountMenuSections } from './AccountMenu/useAccountMenuSections';
import AppLoginQRModal from './AppLoginQR/AppLoginQRModal';
import { NotificationsMenu } from './NotificationsMenu/NotificationsMenu';
import { BatchProcessesDropdown } from '@domains/batchProcesses/shared/components/BatchProcessesDropdown';
import { AutomationDropdown } from '@domains/automation/shared/components/AutomationDropdown';
import { TourEntriesEmptyState } from './TourMenu/TourEntriesEmptyState';
import { useTourEntries } from './TourMenu/useTourEntries';
import classes from './UserNavbar.module.css';
import { useNavbarDispatch, useNavbarSelector } from '../../state/store';
import { useMenuHandlersFactory } from './useMenuHandlers';
import { can, canAny } from '@hooks/useCapability';

export function UserNavbar() {
  const navigationDispatch = useNavigationDispatch();
  const location = useLocation();
  const { user, signout } = useAuth() as AuthContextType;
  const { preferences, updateLanguage } = useUserPreferences() as UserPreferencesContextType;
  const { i18n, t } = useTranslation( 'layout' );
  // The drawer's account block mirrors the desktop AccountMenu, whose strings live
  // in the `account` namespace — see useAccountMenuSections (TV1-2294).
  const { t: tAccount } = useTranslation( 'account' );
  const isOpen = useAIAssistantSelector( s => s.isPanelOpen );
  const { togglePanel } = useAIAssistantActions();

  // Domain state
  const dispatch = useNavbarDispatch();
  const isDrawerOpened = useNavbarSelector( s => s.mobileDrawer.isOpen );
  const openMenus = useNavbarSelector( s => s.openMenus );
  const clickLockedMenus = useNavbarSelector( s => s.clickLockedMenus );
  const accountName = useNavbarSelector( s => s.account.name );
  const qrModalState = useNavbarSelector( s => s.appLoginQRModal );

  // Create menu handlers factory (manages its own timer ref internally)
  const createMenuHandlers = useMenuHandlersFactory();

  // Drawer handlers
  const toggleDrawer = useCallback(() => {
    dispatch(
      { type: 'navbar/drawer/toggle', payload: {}},
      { source: 'user' }
    );
  }, [dispatch]);

  const closeDrawer = useCallback(() => {
    dispatch(
      { type: 'navbar/drawer/close', payload: {}},
      { source: 'user' }
    );
  }, [dispatch]);

  // QR Modal handlers
  const handleAppLogin = useCallback(() => {
    dispatch(
      { type: 'navbar/qrModal/open', payload: {}},
      { source: 'user' }
    );
  }, [dispatch]);

  const closeAppLoginQRModal = () => {
    dispatch(
      { type: 'navbar/qrModal/close', payload: {}},
      { source: 'user' }
    );
  };

  // Account-menu entries shared with the desktop <AccountMenu /> — that component is
  // hidden below `md`, so the drawer renders the SAME descriptors instead of a
  // hand-maintained copy that drifts (TV1-2294).
  const accountMenuSections = useAccountMenuSections({ handleAppLogin, onNavigate: closeDrawer });

  // Get active feature flags
  const activeFeatureFlags = useActiveFeatureFlags();
  // Memoize section flags to prevent recalculation on every render
  const allSectionFlags = useMemo(() => NAVIGATION_CONFIG.map( section => section.flagKey ), []);

  // Memoize active flags to prevent cascading re-renders
  const activeFlags = useMemo(() => ( activeFeatureFlags ? activeFeatureFlags : allSectionFlags ), [ activeFeatureFlags, allSectionFlags ]);

  // Collect all tab flag keys first
  const allTabFlagKeys = useMemo(() => {
    const keys: string[] = [];
    NAVIGATION_CONFIG.forEach( section => {
      if ( section.tabs ) {
        section.tabs.forEach( tab => keys.push( tab.flagKey ));
      }
    });
    return keys;
  }, []);

  // Build tabFlags object by checking if each flag is in activeFeatureFlags
  // This avoids calling hooks inside loops and uses the already-available activeFeatureFlags
  const tabFlags = useMemo(() => {
    const flags: Record<string, boolean> = {};
    allTabFlagKeys.forEach( flagKey => {
      flags[flagKey] = activeFeatureFlags
        ? activeFeatureFlags.includes( flagKey )
        : true;
    });
    return flags;
  }, [ allTabFlagKeys, activeFeatureFlags ]);

  // Build sections with their enabled tabs
  const sectionsWithNavData = useMemo(() => {
    return NAVIGATION_CONFIG
      .filter( section =>
        activeFlags.includes( section.flagKey ) &&
        section.showInNavbar !== false &&
        // ANY-OF over the section key plus every tab key, so a grant of a
        // single tab capability surfaces the section. The per-tab filter below
        // still decides which tabs render. [TV1-2259]
        canAny( user, sectionGateCapabilities( section )))
      .map( section => {
        const sectionEnabled = activeFlags.includes( section.flagKey );

        let enabledTabs: Array<{ id: string; label: string; subtitle?: string }> = [];
        if ( section.tabs && sectionEnabled ) {
          enabledTabs = section.tabs
            .filter( tab => tabFlags[tab.flagKey] && ( !tab.capabilityKey || can( user, tab.capabilityKey )))
            /*
              TV1-2355 — tab strings are resolved in the TAB's namespace, via the
              explicit `ns:key` form.

              This used to be a bare `t( tab.labelKey, ... )`, which resolves
              against THIS component's namespace (`layout`, line 35). Every
              integrations tab declares `translationNamespace: 'integrations'`, so
              the lookup missed and i18next fell back to `defaultLabel` — the
              English string baked into `navigation.config.ts`. The dropdown showed
              "Logistics" and "Invoicing" while the tab strip inside the page, which
              does use the right namespace, showed "Logística" and "Facturación".

              It went unnoticed for so long because the failure is silent and the
              other defaults are camouflage: "Channels" and "ERPs" read as
              acceptable Spanish, so only the two tabs with genuinely different
              translations exposed it.

              The section label above stays a bare `t()` correctly — every SECTION
              in the config declares `translationNamespace: 'layout'`, which is this
              component's namespace. Tabs are the level where they diverge.
              `useAccountMenuSections` already did it this way; this is the navbar
              catching up to its sibling.
            */
            .map( tab => ({
              id: tab.id,
              label: t( `${tab.translationNamespace}:${tab.labelKey}`, tab.defaultLabel ),
              subtitle: tab.subtitleKey && tab.defaultSubtitle
                ? t( `${tab.translationNamespace}:${tab.subtitleKey}`, tab.defaultSubtitle )
                : undefined
            }));
        }

        return {
          section,
          hasTabs: Boolean( section.tabs && section.tabs.length > 0 ),
          enabledTabs,
          slug: stringToLowerCaseDashed( section.id ),
          label: t( section.labelKey, section.defaultLabel )
        };
      });
  }, [ activeFlags, tabFlags, t, user ]);

  const links = useMemo(() =>
    sectionsWithNavData.map(({ section, slug, label, hasTabs, enabledTabs }) => ({
      name: section.id,
      link: section.path,
      label,
      id: `tour-${slug}`,
      hasTabs,
      tabs: enabledTabs,
      onClick: () => {
        if ( hasTabs && enabledTabs.length > 0 ) {
          navigationDispatch(
            { type: 'navigation/request/navigate', payload: { path: `${section.path}/${enabledTabs[0].id}` }},
            { source: 'user' }
          );
        } else {
          navigationDispatch(
            { type: 'navigation/request/navigate', payload: { path: section.path }},
            { source: 'user' }
          );
        }
        closeDrawer();
      }
    })),
    [ sectionsWithNavData, navigationDispatch, closeDrawer ]);

  const { containerRef, measurementRef, rightGroupRef, visibleItems: visibleLinks, overflowItems: overflowLinks } = useNavbarOverflow( links, 100, 0 );

  // Calculate which accordion should be pre-opened on mobile
  const defaultOpenAccordion = useMemo(() => {
    const currentSection = links.find( link =>
      link.hasTabs && link.tabs.some( tab =>
        location.pathname.startsWith( `${link.link}/${tab.id}` )));
    return currentSection?.name;
  }, [ links, location.pathname ]);

  const { setColorScheme } = useMantineColorScheme();
  const computedColorScheme = useComputedColorScheme( 'light' );
  const isDarkScheme = computedColorScheme === 'dark';

  // Tutorials for the mobile drawer — the desktop list lives in AccountMenu's
  // PreferencesSection, both fed by the same hook (TV1-2299).
  const { entries: tourEntries, startTour } = useTourEntries();

  /*
   * TV1-2294 ticket 28 (GAP 4) — ONE gate source for the Language entry.
   *
   * This read used to be `activeFlags.includes( 'languageChange' )` while the
   * desktop `PreferencesSection` read `useBooleanFlagValue( 'languageChange', false )`.
   * Both resolve from the same provider today (`useActiveFeatureFlags` returns
   * the true-valued keys of `getAllFlags()`), so it was latent rather than
   * broken — but "one entry, two gate sources, two hand-written call sites" is
   * the EXACT structure that shipped the dead Subscription button in this very
   * file, and ticket 19 missed this one when it unified the account/user
   * entries. Unified here on ticket 19's choice, `useBooleanFlagValue`.
   */
  const isLanguageChangeEnabled = useBooleanFlagValue( 'languageChange', false );

  const toggleColorScheme = () => {
    setColorScheme( isDarkScheme ? 'light' : 'dark' );
  };

  // i18n.language can carry a region suffix (es-AR) — match on the prefix, not equality.
  const isSpanish = i18n.language?.startsWith( 'es' );

  const changeLanguage = () => {
    updateLanguage( isSpanish ? 'en' : 'es' );
  };

  const handleLogout = () => {
    signout();
  };

  // Menu state change handler for Mantine Menu component
  const handleMenuChange = ( menuKey: string, opened: boolean ) => {
    if ( opened ) {
      dispatch(
        { type: 'navbar/menu/open', payload: { menuKey }},
        { source: 'user' }
      );
    } else {
      dispatch(
        { type: 'navbar/menu/close', payload: { menuKey }},
        { source: 'user' }
      );
    }
  };

  return (
    <header className={classes.header}>
      <div className={classes.inner}>
        <Group gap="xs" className={classes.leftGroup}>
          <Burger opened={isDrawerOpened} onClick={toggleDrawer} size="sm" hiddenFrom="md" />
          <Image src={logo} height={25} width="auto" />
            {/* Below `md` only the logo mark shows — the wordmark's nowrap text
                spilled past `.leftGroup` and was painted over by the right group
                (TV1-2294). */}
            <Group gap={4} visibleFrom="md">
              <Text fw={700} className={classes.application_name}>
                {'Teselly'}
              </Text>
            </Group>

          {/* Hidden measurement container - always contains ALL links for measurement.
              Caged so the absolutely-positioned row cannot inflate the document's
              scrollable overflow region (TV1-2294). The cage carries the positioning;
              the row keeps its natural max-content width so measurement is unaffected. */}
          <div className={classes.measurementCage} aria-hidden="true">
            <Group
              ml={40}
              gap="xs"
              className={classes.linksMeasurement}
              ref={measurementRef}
            >
              {links.map(( link ) => (
                <div key={link.link} data-nav-item>
                  <NavbarLink
                    label={link.label}
                    id={link.id}
                    link={link.link}
                    onClick={() => {}}
                    active={false}
                    hasDropdown={link.hasTabs && link.tabs.length > 0}
                  />
                </div>
              ))}
            </Group>
          </div>

          {/* Visible navigation container */}
          <Group ml={40} gap="xs" className={classes.links} visibleFrom="md" ref={containerRef}>
            {visibleLinks.map(( link ) => {
              const isActive = location.pathname.includes( stringToLowerCaseDashed( link.name ));

              if ( link.hasTabs && link.tabs.length > 0 ) {
                const menuKey = `section-${link.link}`;
                const isMenuOpen = openMenus.has( menuKey );
                const isClickLocked = clickLockedMenus.has( menuKey );
                const menuHandlers = createMenuHandlers( menuKey );

                return (
                  <Menu
                    key={link.link}
                    opened={isMenuOpen && !isClickLocked}
                    onChange={( opened ) => handleMenuChange( menuKey, opened )}
                    position="bottom-start"
                    classNames={{
                      dropdown: classes.menuDropdown,
                      item: classes.menuItem
                    }}
                  >
                    <Menu.Target>
                      <div
                        className={`${classes.menuWithDropdown} ${isMenuOpen ? classes.menuOpen : ''}`}
                        onMouseEnter={() => menuHandlers.handleMouseEnter( isClickLocked )}
                        onMouseLeave={menuHandlers.handleMouseLeave}
                      >
                        <NavbarLink
                          id={link.id}
                          label={link.label}
                          link={link.link}
                          onClick={( e ) => {
                            e?.preventDefault();
                            menuHandlers.handleTitleClick( link.onClick );
                          }}
                          active={isActive}
                          hasDropdown
                        />
                      </div>
                    </Menu.Target>
                    <Menu.Dropdown
                      onMouseEnter={() => menuHandlers.handleMouseEnter( isClickLocked )}
                      onMouseLeave={menuHandlers.handleMouseLeave}
                    >
                      {link.tabs.map(( tab ) => (
                        <Menu.Item
                          key={tab.id}
                          onClick={() => menuHandlers.handleItemClick(() => navigationDispatch(
                            { type: 'navigation/request/navigate', payload: { path: `${link.link}/${tab.id}` }},
                            { source: 'user' }
                          ))}
                        >
                          <div>
                            <div style={{ fontSize: '13px', fontWeight: 500 }}>{tab.label}</div>
                            {tab.subtitle && (
                              <Text
                                c="dimmed"
                                style={{
                                  maxWidth: '220px',
                                  whiteSpace: 'normal',
                                  wordWrap: 'break-word',
                                  lineHeight: '1.2',
                                  fontSize: '11px',
                                  marginTop: '3px'
                                }}
                              >
                                {tab.subtitle}
                              </Text>
                            )}
                          </div>
                        </Menu.Item>
                      ))}
                    </Menu.Dropdown>
                  </Menu>
                );
              }

              return (
                <NavbarLink
                  key={link.link}
                  label={link.label}
                  id={link.id}
                  link={link.link}
                  onClick={link.onClick}
                  active={isActive}
                />
              );
            })}

            {/* More button for overflow items */}
            {overflowLinks.length > 0 && (() => {
              const moreMenuKey = 'more-menu';
              const isMoreMenuOpen = openMenus.has( moreMenuKey );
              const isMoreClickLocked = clickLockedMenus.has( moreMenuKey );
              const moreHandlers = createMenuHandlers( moreMenuKey );

              return (
                <Menu
                  opened={isMoreMenuOpen && !isMoreClickLocked}
                  onChange={( opened ) => handleMenuChange( moreMenuKey, opened )}
                  position="bottom-start"
                  classNames={{
                    dropdown: classes.menuDropdown,
                    item: classes.menuItem
                  }}
                >
                  <Menu.Target>
                    <div
                      className={`${classes.moreButton} ${isMoreMenuOpen ? classes.moreButtonOpen : ''} more-button`}
                      onMouseEnter={() => moreHandlers.handleMouseEnter( isMoreClickLocked )}
                      onMouseLeave={moreHandlers.handleMouseLeave}
                      onClick={() => moreHandlers.handleTitleClick(() => {})}
                    >
                      <Group gap={4}>
                        <span>{t( 'more_navigation_label', 'More' )}</span>
                        <div className={classes.moreIconContainer}>
                          <IconDots size={16} stroke={1.5} className={classes.moreIconDots} />
                          <IconMinus size={16} stroke={1.5} className={classes.moreIconMinus} />
                        </div>
                      </Group>
                    </div>
                  </Menu.Target>
                <Menu.Dropdown
                  onMouseEnter={() => moreHandlers.handleMouseEnter( isMoreClickLocked )}
                  onMouseLeave={moreHandlers.handleMouseLeave}
                >
                  {overflowLinks.map(( link ) => {
                    // Sections with tabs: nested menu with arrow and submenu
                    if ( link.hasTabs && link.tabs.length > 0 ) {
                      const nestedMenuKey = `nested-${link.link}`;
                      const isNestedMenuOpen = openMenus.has( nestedMenuKey );
                      const isNestedClickLocked = clickLockedMenus.has( nestedMenuKey );
                      const nestedHandlers = createMenuHandlers( nestedMenuKey );

                      return (
                        <Menu
                          key={link.link}
                          opened={isNestedMenuOpen && !isNestedClickLocked}
                          onChange={( opened ) => handleMenuChange( nestedMenuKey, opened )}
                          position="right-start"
                          offset={2}
                        >
                          <Menu.Target>
                            <Menu.Item
                              rightSection={<IconChevronRight size={14} stroke={1.5} />}
                              className={classes.menuNestedTrigger}
                              onMouseEnter={() => nestedHandlers.handleMouseEnter( isNestedClickLocked )}
                              onMouseLeave={nestedHandlers.handleMouseLeave}
                              onClick={() => nestedHandlers.handleTitleClick(() => {})}
                            >
                              <div style={{ fontSize: '13px', fontWeight: 500 }}>
                                {link.label}
                              </div>
                            </Menu.Item>
                          </Menu.Target>
                          <Menu.Dropdown
                            onMouseEnter={() => nestedHandlers.handleMouseEnter( isNestedClickLocked )}
                            onMouseLeave={nestedHandlers.handleMouseLeave}
                          >
                            {link.tabs.map(( tab ) => (
                              <Menu.Item
                                key={`${link.link}/${tab.id}`}
                                onClick={() => {
                                  nestedHandlers.handleItemClick(() => navigationDispatch(
                                    { type: 'navigation/request/navigate', payload: { path: `${link.link}/${tab.id}` }},
                                    { source: 'user' }
                                  ));
                                  moreHandlers.handleItemClick(() => {});
                                }}
                                className={classes.menuNestedItem}
                              >
                                <div>
                                  <div style={{ fontSize: '12px', fontWeight: 500 }}>
                                    {tab.label}
                                  </div>
                                  {tab.subtitle && (
                                    <Text
                                      c="dimmed"
                                      style={{
                                        maxWidth: '220px',
                                        whiteSpace: 'normal',
                                        wordWrap: 'break-word',
                                        lineHeight: '1.2',
                                        fontSize: '11px',
                                        marginTop: '3px'
                                      }}
                                    >
                                      {tab.subtitle}
                                    </Text>
                                  )}
                                </div>
                              </Menu.Item>
                            ))}
                          </Menu.Dropdown>
                        </Menu>
                      );
                    }

                    // Simple sections without tabs: show as regular menu item
                    return (
                      <Menu.Item
                        key={link.link}
                        onClick={() => moreHandlers.handleItemClick( link.onClick )}
                      >
                        <div style={{ fontSize: '13px', fontWeight: 500 }}>
                          {link.label}
                        </div>
                      </Menu.Item>
                    );
                  })}
                </Menu.Dropdown>
                </Menu>
              );
            })()}
          </Group>
        </Group>

        <Group gap="xs" className={classes.rightGroup} ref={rightGroupRef}>
          {/* Observation Mode (ADR-068). Renders null unless the account is
              observing, so a live account — which is every account that never
              entered observation — pays nothing for it. */}
          <ObservationModePill />
          {!isOpen && ( activeFlags.includes( 'aiAssistant' )) && (
            <UnstyledButton
              onClick={togglePanel}
              className={classes.aiAssistantButton}
              py={4}
              px={10}
              aria-label={t( 'ai_assistant_aria_label', 'Abrir Asistente IA' )}
            >
              <SparkleIcon size={13} />
              {/* Full text label at ALL widths. It was icon-only below `md` while the
                  wordmark still rendered there and `.rightGroup` overlapped it; the
                  wordmark is now `visibleFrom="md"`, so the space is free (TV1-2294). */}
              {t( 'ai_assistant_label', 'Asistente IA' )}
            </UnstyledButton>
          )}
          <Group style={{ flexShrink: 0 }}>
            {( activeFlags.includes( 'batch-processes-view' )) && <BatchProcessesDropdown />}
            {( activeFlags.includes( 'automation-view' )) && <AutomationDropdown />}
            {( activeFlags.includes( 'notifications' )) && <NotificationsMenu />}
          </Group>
          {/* Theme, language and tutorials moved into AccountMenu's Preferences
              section (TV1-2299) — they are settings, not workspace tools. */}
          <Group visibleFrom="md" gap="xs" style={{ flexShrink: 0 }}>
            <AccountMenu handleAppLogin={handleAppLogin}/>
          </Group>
        </Group>


        {/* MOBILE/SMALLSCREEN */}
        <Drawer
          opened={isDrawerOpened}
          onClose={closeDrawer}
          title={
            <Group gap="sm">
              <Image src={logo} height={25} width="auto" />
              <Text fw={700} className={classes.application_name}>
                {accountName}
              </Text>
            </Group>
          }
          padding="md"
          size="sm"
          hiddenFrom="md"
          classNames={{ body: classes.drawerBody }}
        >
          <div className={classes.drawerContainer}>
            <div>
              <div className={classes.mobileLinks}>
                {links.map(( link ) => {
                  const isActive = location.pathname.includes( stringToLowerCaseDashed( link.name ));

                  if ( link.hasTabs && link.tabs.length > 0 ) {
                    return (
                      <Accordion
                        key={link.link}
                        variant="default"
                        defaultValue={defaultOpenAccordion === link.name ? link.name : undefined}
                        className={classes.mobileAccordion}
                        classNames={{
                          item: classes.accordionItem,
                          control: classes.accordionControl,
                          chevron: classes.accordionChevron,
                          // panel: classes.accordionPanel,
                          content: classes.accordionContent,
                          label: classes.accordionLabel
                        }}
                      >
                        <Accordion.Item value={link.name}>
                          <Accordion.Control>
                            <span style={{ fontSize: '13px', fontWeight: 500 }}>{link.label}</span>
                          </Accordion.Control>
                          <Accordion.Panel>
                            <div style={{ display: 'flex', flexDirection: 'column' }}>
                              {link.tabs.map(( tab ) => (
                                <NavbarLink
                                  key={tab.id}
                                  label={tab.label}
                                  link={`${link.link}/${tab.id}`}
                                  onClick={() => {
                                    navigationDispatch(
                                      { type: 'navigation/request/navigate', payload: { path: `${link.link}/${tab.id}` }},
                                      { source: 'user' }
                                    );
                                    closeDrawer();
                                  }}
                                  active={location.pathname.startsWith( `${link.link}/${tab.id}` )}
                                  subtitle={tab.subtitle}
                                />
                              ))}
                            </div>
                          </Accordion.Panel>
                        </Accordion.Item>
                      </Accordion>
                    );
                  }

                  return (
                    <NavbarLink
                      key={link.link}
                      id={link.id}
                      label={link.label}
                      link={link.link}
                      onClick={link.onClick}
                      active={isActive}
                    />
                  );
                })}
              </div>
              {/* Account / user entries — rendered from useAccountMenuSections, the
                  same descriptor list the desktop AccountMenu renders. Add entries
                  there, not here (TV1-2294). */}
              {accountMenuSections.map(( section ) => (
                <Fragment key={section.key}>
                  <Divider
                    label={section.showLabel ? section.label : undefined}
                    labelPosition="left"
                    my="sm"
                  />
                  <div className={classes.mobileLinks}>
                    {section.entries.map(( entry ) => {
                      const EntryIcon = entry.icon;
                      const path = entry.path;
                      const entryIcon = <EntryIcon style={{ width: rem( 16 ), height: rem( 16 ) }} stroke={1.5} />;

                      /*
                        TV1-2294 ticket 37 — an account entry whose target has
                        tabs (Account Settings) renders as an Accordion, exactly
                        like a navbar section with tabs a few lines above. The
                        tabs come from the entry descriptor, which derives them
                        from NAVIGATION_CONFIG — the SAME declaration the in-page
                        SideMenu rail reads via `useTabNavigation`. There is no
                        second list to keep in sync.
                      */
                      if ( entry.tabs.length > 0 ) {
                        return (
                          <Accordion
                            key={entry.key}
                            variant="default"
                            defaultValue={path && location.pathname.startsWith( path ) ? entry.key : undefined}
                            className={classes.mobileAccordion}
                            classNames={{
                              item: classes.accordionItem,
                              control: classes.accordionControl,
                              chevron: classes.accordionChevron,
                              content: classes.accordionContent,
                              label: classes.accordionLabel
                            }}
                          >
                            <Accordion.Item value={entry.key}>
                              <Accordion.Control icon={entryIcon}>
                                <span style={{ fontSize: '13px', fontWeight: 500 }}>{entry.label}</span>
                              </Accordion.Control>
                              <Accordion.Panel>
                                <div style={{ display: 'flex', flexDirection: 'column' }}>
                                  {entry.tabs.map(( tab ) => (
                                    <NavbarLink
                                      key={tab.id}
                                      label={tab.label}
                                      link={tab.path}
                                      onClick={tab.onClick}
                                      active={location.pathname.startsWith( tab.path )}
                                    />
                                  ))}
                                </div>
                              </Accordion.Panel>
                            </Accordion.Item>
                          </Accordion>
                        );
                      }

                      return (
                        <NavbarLink
                          key={entry.key}
                          label={entry.label}
                          link={path ?? ''}
                          icon={entryIcon}
                          onClick={entry.onClick}
                          active={path ? location.pathname.startsWith( path ) : false}
                        />
                      );
                    })}
                  </div>
                </Fragment>
              ))}
              <Divider
                label={tAccount( 'preferences_label', 'Preferencias' )}
                labelPosition="left"
                my="sm"
              />
              <div className={classes.mobileLinks}>
                 {/* Drawer equivalents of the Preferences section (TV1-2299) — the
                     desktop entries live in AccountMenu and are hidden below md. */}
                 <NavbarLink
                   key="color_scheme"
                   label={isDarkScheme
                     ? t( 'light_mode_label', 'Modo claro' )
                     : t( 'dark_mode_label', 'Modo oscuro' )}
                   link=""
                   icon={isDarkScheme
                     ? <IconSun style={{ width: rem( 16 ), height: rem( 16 ) }} stroke={1.5} />
                     : <IconMoon style={{ width: rem( 16 ), height: rem( 16 ) }} stroke={1.5} />}
                   onClick={toggleColorScheme}
                 />
                 {isLanguageChangeEnabled &&
                   <NavbarLink
                     key="language"
                     label={t( 'language_label', 'Idioma: {{language}}', { language: isSpanish ? 'ES' : 'EN' })}
                     link=""
                     icon={<IconLanguage style={{ width: rem( 16 ), height: rem( 16 ) }} stroke={1.5} />}
                     onClick={changeLanguage}
                   />
                 }
                 {/*
                   TV1-2294 ticket 28 (GAP 3) — the Tutoriales block is ALWAYS
                   rendered. It used to sit behind `tourEntries.length > 0`, so
                   with no tutorials the drawer showed nothing at all while
                   desktop showed a proper empty state — a direct violation of
                   `.claude/rules/design-system.md` § Empty States. The panel now
                   renders the SAME shared `TourEntriesEmptyState` the desktop
                   Preferences submenu renders.
                 */}
                 <Accordion
                   variant="default"
                   className={classes.mobileAccordion}
                   classNames={{
                     item: classes.accordionItem,
                     control: classes.accordionControl,
                     chevron: classes.accordionChevron,
                     content: classes.accordionContent,
                     label: classes.accordionLabel
                   }}
                 >
                   <Accordion.Item value="tutorials">
                     <Accordion.Control
                       icon={<IconProgressHelp style={{ width: rem( 16 ), height: rem( 16 ) }} stroke={1.5} />}
                     >
                       <span style={{ fontSize: '13px', fontWeight: 500 }}>
                         {t( 'tutorials_label', 'Tutoriales' )}
                       </span>
                     </Accordion.Control>
                     <Accordion.Panel>
                       {tourEntries.length === 0 ? (
                         <TourEntriesEmptyState />
                       ) : (
                         <div style={{ display: 'flex', flexDirection: 'column' }}>
                           {tourEntries.map(( entry ) => (
                             <NavbarLink
                               key={entry.key}
                               label={entry.name}
                               link=""
                               subtitle={entry.description}
                               /*
                                 TV1-2294 ticket 28 (GAP 3) — per-tour status
                                 badge. Desktop renders `entry.badge` off the
                                 SAME `useTourEntries()` descriptor; the drawer
                                 rendered name + description only, so a
                                 tutorial's status ("Completado" / "Continuar" /
                                 "Omitido") was invisible on mobile. Same
                                 variant, colour and label — no second source.
                               */
                               rightSection={
                                 <Badge size="xs" variant={entry.badge.variant} color={entry.badge.color}>
                                   {entry.badge.label}
                                 </Badge>
                               }
                               onClick={() => {
                                 startTour( entry.key );
                                 closeDrawer();
                               }}
                             />
                           ))}
                         </div>
                       )}
                     </Accordion.Panel>
                   </Accordion.Item>
                 </Accordion>
              </div>
            </div>

            <UnstyledButton className={classes.link} onClick={handleLogout}>
              <Group gap="xs">
                <IconLogout style={{ width: rem( 16 ), height: rem( 16 ) }} stroke={1.5} />
                {/* `t( key ) || fallback` can never fire — i18next returns the key
                    itself when it is missing, which is truthy (TV1-2294). */}
                <Text size="sm">{t( 'log_out', 'Cerrar sesión' )}</Text>
              </Group>
            </UnstyledButton>
          </div>
        </Drawer>


        {/* App Login QR Modal */}
        <AppLoginQRModal
          opened={qrModalState.isOpen}
          onClose={closeAppLoginQRModal}
          qrCodeImage={qrModalState.qrCodeImage}
          loading={qrModalState.isLoading}
        />
      </div>
    </header>
  );
}
